import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@utils/cn';

import { popoverContentClasses } from './Popover.styles';
import type { PopoverAlign, PopoverProps, PopoverSide } from './Popover.types';

const SIDE_OFFSET_DEFAULT = 8;

function getPosition(
  triggerRect: DOMRect,
  contentRect: DOMRect,
  side: PopoverSide,
  align: PopoverAlign,
  sideOffset: number
): { top: number; left: number } {
  const { width: tw, height: th, top: tt, left: tl, right: tr, bottom: tb } = triggerRect;
  const { width: cw, height: ch } = contentRect;

  const alignLeft = (): number => {
    if (align === 'start') {
      return tl;
    }
    if (align === 'end') {
      return tr - cw;
    }
    return tl + tw / 2 - cw / 2;
  };

  const alignTop = (): number => {
    if (align === 'start') {
      return tt;
    }
    if (align === 'end') {
      return tb - ch;
    }
    return tt + th / 2 - ch / 2;
  };

  switch (side) {
    case 'top':
      return { top: tt - ch - sideOffset, left: alignLeft() };
    case 'left':
      return { top: alignTop(), left: tl - cw - sideOffset };
    case 'right':
      return { top: alignTop(), left: tr + sideOffset };
    case 'bottom':
    default:
      return { top: tb + sideOffset, left: alignLeft() };
  }
}

// Keep the popover within the visible viewport with some padding.
function clampViewport(
  top: number,
  left: number,
  contentRect: DOMRect
): { top: number; left: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const padding = 8;

  return {
    top: Math.min(Math.max(top, padding), vh - contentRect.height - padding),
    left: Math.min(Math.max(left, padding), vw - contentRect.width - padding)
  };
}

/** The shadow DOM needs a portal container so the popover
 * renders within the same shadow root element.
 */
function getShadowRootPortal(node: Element | null): Element | null {
  if (!node) {
    return null;
  }
  const root = node.getRootNode();
  if (!(root instanceof ShadowRoot)) {
    return null;
  }
  let container = root.querySelector('[data-popover-portal]') as HTMLElement | null;
  if (!container) {
    container = document.createElement('div');
    container.setAttribute('data-popover-portal', '');
    container.style.display = 'contents';
    root.appendChild(container);
  }
  return container;
}

export function Popover({
  children,
  content,
  side = 'bottom',
  align = 'center',
  sideOffset = SIDE_OFFSET_DEFAULT,
  open: controlledOpen,
  onOpenChange,
  defaultOpen = false,
  closeOnOutsideClick = true,
  closeOnEscape = true,
  className,
  contentClassName,
  disabled = false
}: PopoverProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const isControlled = controlledOpen !== undefined;
  // Derive effective open state — disabled suppresses without needing an effect.
  const open = (isControlled ? controlledOpen : uncontrolledOpen) && !disabled;

  const triggerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [portalContainer, setPortalContainer] = useState<Element | null>(null);

  // Callback ref: store the trigger node and detect shadow root on mount.
  const triggerCallbackRef = useCallback((node: HTMLDivElement | null) => {
    triggerRef.current = node;
    if (node) {
      setPortalContainer(getShadowRootPortal(node));
    }
  }, []);

  // Clean up the portal container on unmount if it's empty.
  useEffect(() => {
    return () => {
      if (portalContainer && portalContainer.childNodes.length === 0) {
        portalContainer.remove();
      }
    };
  }, [portalContainer]);

  const setOpen = useCallback(
    (value: boolean) => {
      if (!value) {
        setPosition(null);
      }
      if (!isControlled) {
        setUncontrolledOpen(value);
      }
      onOpenChange?.(value);
    },
    [isControlled, onOpenChange]
  );

  const updatePosition = useCallback(() => {
    if (!triggerRef.current || !contentRef.current) {
      return;
    }
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const contentRect = contentRef.current.getBoundingClientRect();
    const pos = getPosition(triggerRect, contentRect, side, align, sideOffset);
    setPosition(clampViewport(pos.top, pos.left, contentRect));
  }, [side, align, sideOffset]);

  useLayoutEffect(() => {
    if (open) {
      updatePosition();
    }
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleEscape = (e: KeyboardEvent) => {
      if (closeOnEscape && e.key === 'Escape') {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, closeOnEscape, setOpen]);

  useEffect(() => {
    if (!open || !closeOnOutsideClick) {
      return;
    }
    const contains = (ref: React.RefObject<HTMLDivElement | null>, node: Node) =>
      ref.current === node || ref.current?.contains(node);

    const handleClickOutside = (e: MouseEvent) => {
      const path = e.composedPath?.() ?? [e.target];
      // Handles clicks inside both the trigger or content, even if nested in shadow DOM.
      const isInside = path.some(
        (node) => node instanceof Node && (contains(triggerRef, node) || contains(contentRef, node))
      );
      if (!isInside) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, closeOnOutsideClick, setOpen]);

  const handleTriggerClick = () => {
    if (disabled) {
      return;
    }
    setOpen(!open);
  };

  const contentElement = open ? (
    <div
      ref={contentRef}
      role="dialog"
      aria-modal="false"
      data-side={side}
      data-align={align}
      className={cn(
        popoverContentClasses({ visibility: position ? 'visible' : 'hidden' }),
        contentClassName
      )}
      style={{
        top: position?.top ?? 0,
        left: position?.left ?? 0
      }}
      // Prevent mousedown from bubbling so it doesn't trigger outside-click close.
      onMouseDown={(e) => e.stopPropagation()}
    >
      {content}
    </div>
  ) : null;

  return (
    <>
      <div
        ref={triggerCallbackRef}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={handleTriggerClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleTriggerClick();
          }
        }}
        className={className}
      >
        {children}
      </div>
      {/* Portal into shadow root when available, otherwise render inline. */}
      {portalContainer ? createPortal(contentElement, portalContainer) : contentElement}
    </>
  );
}
